/**
 * Admin Middleware
 * Проверяет наличие админской сессии для доступа к админ-панели
 * ✅ ОБНОВЛЕНО: Использует useAdminSessionManager (localStorage вместо cookies)
 * ✅ УЛУЧШЕНО: Сохраняет текущий URL для редиректа после авторизации
 * ✅ ИСПРАВЛЕНО: Ждёт инициализации токенов перед проверкой
 */

import consola from 'consola'
import { useAdminSessionManager } from '@features/auth'
import { canAccessAdminPath, defaultAdminPathForRole } from '@shared/config'

export default defineNuxtRouteMiddleware(async (to, from) => {
  // Только на клиенте
  if (!process.client) return
  
  const localePath = useLocalePath()
  
  consola.log('🔍 [AdminMiddleware] Проверка доступа к:', to.fullPath, {
    from: from.fullPath,
    path: to.path,
  })
  
  // Проверяем наличие админской сессии
  const adminSessionManager = useAdminSessionManager()
  
  // ⚡ КРИТИЧЕСКИ ВАЖНО: Ждём инициализации токенов из localStorage
  consola.log('⏳ [AdminMiddleware] Ожидание инициализации токенов...')
  await adminSessionManager.waitForInit()
  consola.log('✅ [AdminMiddleware] Инициализация токенов завершена')
  
  // Подробное логирование состояния сессии
  const hasSession = adminSessionManager.hasAdminSession.value
  consola.log('🔐 [AdminMiddleware] Состояние сессии:', {
    hasSession,
    accessToken: adminSessionManager.adminAccessToken.value ? 'есть' : 'нет',
    refreshToken: adminSessionManager.adminRefreshToken.value ? 'есть' : 'нет',
  })

  if (!hasSession) {
    consola.warn('❌ [AdminMiddleware] Админская сессия отсутствует, редирект на /administration/home')
    
    // Сохраняем текущий URL в query параметре для редиректа после авторизации
    const redirectUrl = to.fullPath
    
    return navigateTo({
      path: localePath('/administration/home'),
      query: redirectUrl !== localePath('/administration') && redirectUrl !== localePath('/administration/home') 
        ? { redirect: redirectUrl } 
        : undefined,
    })
  }

  // 🔒 Гейтинг по роли: ограниченные роли (например, manager) видят не все разделы
  const role = adminSessionManager.getAdminRole()
  if (!canAccessAdminPath(role, to.path)) {
    const fallback = localePath(defaultAdminPathForRole(role))
    consola.warn(
      `⛔ [AdminMiddleware] Роль "${role ?? 'неизвестна'}" не имеет доступа к ${to.path}, редирект на ${fallback}`,
    )
    return navigateTo(fallback)
  }

  consola.log('✅ [AdminMiddleware] Админская сессия активна, доступ разрешен к:', to.path)
})
